import { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Button, Card, CardActions, TextField } from '@mui/material';

import { apiUrls } from '../../../constans/apiUrls';
import useApiService from '../../../component/hooks/useApiService';
import { useAppSnackbar } from '../../../component/providers/SnackbarProvider';
import { userBoostsAssignmentsListColumns } from '../../../component/common/tableHelper';
import DataGrid from '../../../component/ui/data-grid/DataGrid';
import stringFormat from '../../../component/common/stringFormat';

const UserBoostsAssignmentsList = ({ data, close, adminPanelAction, openType, bucket }) => {
    const { executeGet, executePost } = useApiService();
    const [rows, setRows] = useState([]);
    const [filter] = useState({});
    const { showSnackbar } = useAppSnackbar();
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [usageAmount, setUsageAmount] = useState('');
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [setError] = useState(null);

    const isUsageMode = openType === 'Usage';
    const isViewMode = openType === 'View';
    const isDetailMode = openType === 'AssignmentDetail';

    const columns = useMemo(
        () => userBoostsAssignmentsListColumns(),
        []
    );

    const fetchData = async (
        filterParams = filter,
        page = paginationModel.page,
        pageSize = paginationModel.pageSize
    ) => {
        setLoading(true);
        try {
            if (isViewMode) {
                const response = await executeGet({
                    url: apiUrls.BoostAssignments,
                    params: {
                        ...filterParams,
                        user_id: data.id,
                        offset: page * pageSize,
                        limit: pageSize,
                    },
                });
                setRows(response.items ?? []);
                setTotalCount(response.meta?.total ?? 0);
            } else {
                const response = await executeGet({
                    url: stringFormat(apiUrls.BoostAssignmentsId, data.assignment_id),
                });
                setRows([response] ?? []);
                setTotalCount(1);
            }
        } catch (err) {
            console.error('Veri alınamadı:', err);
            setError('Veri yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(filter, paginationModel.page, paginationModel.pageSize);
    }, [paginationModel.page, paginationModel.pageSize, filter, openType, data?.assignment_id, data?.id]);

    const selectedRow = useMemo(() => {
        if (!isViewMode) return rows?.[0] ?? null;
        if (rowSelectionModel.length === 0) return null;
        return rows.find((row) => row.id === rowSelectionModel[0]) || null;
    }, [isViewMode, rows, rowSelectionModel]);

    useEffect(() => {
        setUsageAmount('');
        setNote('');
    }, [selectedRow, openType]);

    const usageBoostToUser = async () => {
        if (!isUsageMode) return;
        if (!usageAmount || !note) {
            showSnackbar('Kullanım miktarı ve not zorunludur.', 'error');
            return;
        }
        if (!selectedRow?.id) {
            showSnackbar('Geçerli bir kayıt bulunamadı.', 'error');
            return;
        }
        try {
            const res = await executePost({
                url: stringFormat(apiUrls.BoostAssignmentsUsageToUser, selectedRow.id),
                data: {
                    qty: Number(usageAmount),
                    ticket_id: data.id,
                    note: note,
                },
            });
            if (res) close && close(res);
        } catch (err) {
            if (err.response.status === 409) {
                showSnackbar("Kota aşıldı.Kullanım için yeterli boost bulunmamaktadır.", 'error');
                close && close();
            }
            else {
                showSnackbar('Boost kullanımında bir hata oluştu.', 'error');
            }

        }
    };

    const actionList = [
        { name: 'Usage', label: 'Kullan', order: 2 },
        { name: 'Cancel', variant: 'text', label: 'Vazgeç', order: 0 },
    ];

    const cancelOnly = [{ name: 'Cancel', variant: 'text', label: 'Vazgeç', order: 0 }];

    const filteredActionList = () => {
        if (isViewMode) return cancelOnly;

        if (isUsageMode) {
            return actionList.filter((a) => a.name === 'Usage' || a.name === 'Cancel');
        }
        if (isDetailMode) {
            return actionList.filter((a) => a.name === 'Cancel');
        }
        if (!selectedRow) return actionList.filter((a) => a.name === 'Cancel');
        const { assigned } = selectedRow;
        if (!assigned) return actionList.filter((a) => a.name === 'Usage' || a.name === 'Cancel');
        return actionList.filter((a) => a.name === 'Cancel');
    };

    const onActionClick = useCallback(
        (action) => {
            switch (action.name) {
                case 'Usage':
                    if (isUsageMode) usageBoostToUser();
                    break;
                case 'Cancel':
                    close && close();
                    break;
                default:
                    break;
            }
        },
        [isUsageMode, selectedRow, usageAmount, note, close]
    );

    return (
        <Card sx={{ display: 'flex', flexDirection: 'column', height: 'auto' }}>
            <div style={{ height: 400, width: '100%', marginTop: 20 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    rowCount={totalCount}
                    paginationMode={isViewMode ? 'server' : 'client'}
                    loading={loading}
                    getRowId={(row, index) => row.id ?? index + 1}
                    rowSelectionModel={isViewMode ? rowSelectionModel : []}
                    onRowSelectionModelChange={isViewMode ? setRowSelectionModel : undefined}
                />
            </div>

            {isUsageMode && selectedRow && (
                <Box sx={{ display: 'flex', gap: 2, px: 2, pt: 2 }}>
                    <TextField
                        label="Kullanım Miktarı"
                        type="number"
                        fullWidth
                        inputProps={{
                            min: 0,
                            step: 1,
                            pattern: "[0-9]*"
                        }}
                        value={usageAmount}
                        onKeyDown={(e) => {
                            if (["-", "+", ",", "."].includes(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                                setUsageAmount(e.target.value)
                            }
                        }}
                    />
                    <TextField label="Not" value={note} onChange={(e) => setNote(e.target.value)} fullWidth />
                </Box>
            )}

            <CardActions
                sx={{
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #e0e0e0',
                    p: 2,
                    backgroundColor: '#fff',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                }}
            >
                {filteredActionList()
                    .sort((a, b) => a.order - b.order)
                    .map((action) => (
                        <Button
                            key={action.name}
                            type="button"
                            variant={action.variant || 'contained'}
                            onClick={() => onActionClick(action)}
                            sx={{
                                ...(action.variant && {
                                    textTransform: 'none',
                                    color: '#1976d2',
                                    border: 'none',
                                    fontWeight: '500',
                                    '&:hover': { backgroundColor: '#e3f2fd' },
                                }),
                                ...(!action.variant && {
                                    textTransform: 'none',
                                    backgroundColor: '#1976d2',
                                    color: 'white',
                                    borderRadius: '10px',
                                    fontWeight: 'bold',
                                }),
                            }}
                        >
                            {action.label}
                        </Button>
                    ))}
            </CardActions>
        </Card>
    );
};

export default UserBoostsAssignmentsList;
